import { Component } from "react";
import { Box, Typography } from "@mui/material";
import { logError } from "../services/logging";

interface Props {
  children?: React.ReactNode;
  errorContent?: React.ReactNode;
  onError?: (error: Error) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  recoveryAttempt: number;
}

// Translation crashes arrive in bursts (a translator sweeping the page), so the
// strike budget is per-burst. Without this the counter only ever climbed, and a
// long session that spent its three strikes in the first minute showed
// "Something went wrong..." for every later crash it could have recovered from.
const RECOVERY_WINDOW_MS = 10000;
const MAX_RECOVERIES_PER_WINDOW = 3;

// Chrome Translate wraps text nodes in <font> tags, breaking React's DOM reconciliation
// on component unmount/swap. Detect these errors so we can auto-recover.
function isBrowserExtensionDomError(error: Error): boolean {
  const msg = error?.message || "";
  return (
    (msg.includes("removeChild") || msg.includes("insertBefore")) &&
    msg.includes("not a child of this node")
  );
}

class ErrorBoundary extends Component<Props, State> {
  private windowStartedAt = 0;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, recoveryAttempt: 0 };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    // Chrome Translate / browser extensions corrupt DOM nodes, causing
    // removeChild/insertBefore failures. installTranslationGuard now stops most of
    // these before they throw; this stays as the backstop. Auto-recover by
    // re-rendering fresh, with a per-burst strike budget.
    if (isBrowserExtensionDomError(error)) {
      const now = Date.now();
      const inSameBurst = now - this.windowStartedAt < RECOVERY_WINDOW_MS;
      const attempt = inSameBurst ? this.state.recoveryAttempt + 1 : 1;

      if (attempt <= MAX_RECOVERIES_PER_WINDOW) {
        if (!inSameBurst) this.windowStartedAt = now;
        this.setState({
          hasError: false,
          error: undefined,
          recoveryAttempt: attempt,
        });
        return;
      }
    }

    if (this.props.onError) {
      this.props.onError(error);
    } else {
      logError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.errorContent) {
        return this.props.errorContent;
      }

      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <h1>Something went wrong...</h1>
            <div>Please try again or contact us in the chat.</div>
            {this.state.error?.message && (
              <Typography
                color="text.secondary"
                variant="caption"
                component="div"
                sx={{ mt: 3 }}
              >
                Error: {this.state.error.message}
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
